import React, { useEffect, useRef } from 'react'
import { intermediatePageArray } from 'components/tables/tableTools'

import type { Table as ReactTable } from '@tanstack/table-core'
import {
  Row,
  Column,
  createColumnHelper,
  PaginationState,
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  getFilteredRowModel,
  getPaginationRowModel,
  ColumnDef,
  OnChangeFn,
  flexRender,
  RowSelectionTableState,
  RowSelectionState
} from '@tanstack/react-table'

declare module '@tanstack/react-table' {
  interface ColumnMeta {
    className?: string
  }
}

export default function UsersTable(props) {

  const rerender = React.useReducer(() => ({}), {})[1]

  const rowSelection = props.rowSelection;
  const setRowSelection = props.setRowSelection;
  const data = props.data;
  const columns = props.columns;
  const refreshData = props.refreshData
  const getUnits = props.getUnits;
  const pageSize = props.pageSize;

  //Table Control
  const [sorting, setSorting] = React.useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = React.useState()
  const [columnVisibility, setColumnVisibility] = React.useState({})

  const searchFilter = (e) => {
    setGlobalFilter(e.currentTarget.value);
  }
  const [showColumnSelection, setShowColumnSelection] = React.useState(false)
  const toggleColumnSelection = (e) => {
    setShowColumnSelection(!showColumnSelection);
  }
  const columnSelection = useRef(null)
  const pageSizeSelection = useRef(null)
  const closeSelection = (e) => {
    if (columnSelection.current && showColumnSelection && !columnSelection.current.contains(e.target)) {
      setShowColumnSelection(false);
    }
    if (pageSizeSelection.current && showPageSizeSelection && !pageSizeSelection.current.contains(e.target)) {
      setShowPageSizeSelection(false);
    }
  }
  useEffect(() => {
    document.addEventListener('mousedown', closeSelection)
    return () => {
      document.removeEventListener('mousedown', closeSelection);
    };
  }, [])
  const [isFullscreen, setIsFullscreen] = React.useState(false)
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  }
  const [selectedPageSize, setSelectedPageSize] = React.useState(pageSize || 10)
  const [showPageSizeSelection, setShowPageSizeSelection] = React.useState(false)
  const togglePageSizeSelection = (e) => {
    setShowPageSizeSelection(!showPageSizeSelection);
  }
  useEffect(() => {
    setShowPageSizeSelection(false);
    table.setPageSize(Number(selectedPageSize))
  }, [selectedPageSize])

  //Table Creation
  const table = useReactTable({
    data,
    columns,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    state: {
      sorting,
      globalFilter,
      columnVisibility,
      rowSelection: rowSelection,
    },
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true, //enable row selection for all rows
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    //debugTable: true,
  })

  return (
    <div>
      <div className={`bootstrap-table bootstrap4 ${isFullscreen ? 'fullscreen' : ''}`}>
        <div className="fixed-table-toolbar">
          <div className="bs-bars float-left">
            <div id="toolbar" className="">
              <button id="remove" className="btn btn-danger d-none" disabled="">
                <i className="glyphicon glyphicon-remove"></i> <span data-locale="Delete">Delete</span>
              </button>

              { props.create &&
                <button className="btn btn-success" data-toggle="modal" data-target="#createUnit">
                <span data-locale="Create" onClick={props.create}>{props.createLabel}</span>
              </button>
              }
            </div>
          </div>
          <div className="columns columns-right btn-group float-right">
            {/* 
            <button className="btn btn-secondary" type="button" name="paginationSwitch" aria-label="Pagination Switch" title="Hide/Show pagination"><i className="fa fa-caret-square-down"></i></button>
            <button className="btn btn-secondary" type="button" name="toggle" aria-label="Toggle" title="Toggle"><i className="fa fa-toggle-off"></i></button>
          */}
            <button className="btn btn-secondary" type="button" name="refresh" title="Refresh" onClick={() => refreshData()}><i className="fa fa-sync"></i></button>
            <button className="btn btn-secondary" type="button" name="fullscreen" title="Fullscreen" onClick={toggleFullscreen}><i className={`fa ${!isFullscreen ? 'fa-arrows-alt' : 'fa-arrows-alt'}`}></i></button>

            <div className="keep-open btn-group" title="Columns">
              <button className="btn btn-secondary dropdown-toggle" onClick={toggleColumnSelection} type="button" data-toggle="dropdown" aria-label="Columns" title="Columns"><i className="fa fa-th-list"></i><span className="caret"></span></button>
              <div ref={columnSelection} className={`dropdown-menu dropdown-menu-right ${showColumnSelection ? 'show' : ''}`}>
                {table.getAllLeafColumns().map(column => {
                  return (
                    <div key={column.id} className="px-1">
                      <label className="dropdown-item dropdown-item-marker">
                        <input
                          {...{
                            type: 'checkbox',
                            checked: column.getIsVisible(),
                            onChange: column.getToggleVisibilityHandler(),
                          }}
                        />
                        {' '} {column.id}
                      </label>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          <div className="float-right search btn-group">
            <input className="form-control search-input" type="text" placeholder="Search" autoComplete="off" onChange={searchFilter} />
          </div>
        </div>
        <div className="fixed-table-container">
          <div className="fixed-table-body">
            <table className="table table-bordered table-hover">
              <thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => {
                      return (
                        <th
                          key={header.id}
                          colSpan={header.colSpan}
                          style={{ textAlign: 'center' }}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {header.isPlaceholder ? null : (
                            <div className={`th-inner sortable both ${header.column.getIsSorted()}`}>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </div>
                          )}
                        </th>
                      )
                    })}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => {
                  return (
                    <tr key={row.id}>
                      {row.getVisibleCells().map(cell => {
                        return (
                          <td key={cell.id} className={cell.column.columnDef.meta?.className}>
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="fixed-table-pagination" >
          <div className="float-left pagination-detail">

            <span className="pagination-info">Showing {selectedPageSize * table.getState().pagination.pageIndex + 1}
              {' '} to {selectedPageSize * table.getState().pagination.pageIndex + table.getRowModel().rows.length}
              {' '} of {table.getFilteredRowModel().rows.length} rows
            </span>
            <div className="page-list">
              <div className="btn-group dropdown dropup">
                <button className="btn btn-secondary dropdown-toggle" type="button" onClick={togglePageSizeSelection}>
                  <span className="page-size">{selectedPageSize}</span>
                  <span className="caret"></span>
                </button>
                <div ref={pageSizeSelection} className={`dropdown-menu ${showPageSizeSelection ? 'show' : ''}`} >
                  {[10, 25, 50].map(pageSize => (
                    <a key={pageSize} className={`dropdown-item ${pageSize == selectedPageSize ? 'active' : ''}`} onClick={() => setSelectedPageSize(pageSize)}>{pageSize}</a>
                  ))}
                </div>
              </div>
              {' '} rows per page
            </div>
          </div>

          <div className="float-right pagination">
            {table.getPageCount() > 1 &&
              <ul className="pagination">
                <li className={`page-item page-pre ${!table.getCanPreviousPage() ? 'disabled' : ''}`}><a className="page-link" onClick={() => table.previousPage()}>‹</a></li>
                <li className={`page-item page-pre ${table.getState().pagination.pageIndex == 0 ? 'active' : ''}`}><a className="page-link" onClick={() => table.setPageIndex(0)}>1</a></li>
                {table.getState().pagination.pageIndex > 3 && table.getPageCount() > 5 &&
                  <li className="page-item page-first-separator disabled"><a className="page-link">...</a></li>
                }
                {intermediatePageArray(table.getState().pagination.pageIndex, table.getPageCount()).map(pageIdx => (
                  <li key={pageIdx} className={`page-item ${pageIdx == (table.getState().pagination.pageIndex) ? 'active' : ''}`}><a className="page-link" onClick={() => table.setPageIndex(pageIdx)}>{pageIdx + 1}</a></li>
                ))}

                {table.getState().pagination.pageIndex < (table.getPageCount() - 4) && table.getPageCount() > 5 &&
                  <li className="page-item page-first-separator disabled"><a className="page-link">...</a></li>
                }
                <li className={`page-item page-pre ${table.getPageCount() == (table.getState().pagination.pageIndex + 1) ? 'active' : ''}`}><a className="page-link" onClick={() => table.setPageIndex(table.getPageCount() - 1)}>{table.getPageCount()}</a></li>
                <li className={`page-item page-next ${!table.getCanNextPage() ? 'disabled' : ''}`}><a className="page-link" onClick={() => table.nextPage()}>›</a></li>
              </ul>
            }
          </div>
        </div>
      </div>
    </div>
  )
}

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Failed to find the root element')
